import React, { useState, useEffect } from 'react';
import { Bell, BellOff, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PushNotificationManager = () => {
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      console.log('[Push] Bu tarayıcı bildirimleri desteklemiyor');
      return;
    }

    // Check current permission status
    setPermission(Notification.permission);

    // Check if already subscribed
    checkSubscription();

    // Show prompt after 30 seconds if not subscribed
    const promptTimer = setTimeout(() => {
      if (Notification.permission === 'default' && !subscribed) {
        setShowPrompt(true);
      }
    }, 30000);

    return () => clearTimeout(promptTimer);
  }, [subscribed]);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        setSubscribed(true);
        console.log('[Push] Mevcut subscription bulundu');
      }
    } catch (error) {
      console.error('[Push] Subscription kontrolü hatası:', error);
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribeToPush = async () => {
    setLoading(true);
    
    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        console.log('[Push] Bildirim izni reddedildi');
        setShowPrompt(false);
        setLoading(false);
        return;
      }

      // Get VAPID public key from backend
      const vapidResponse = await axios.get(`${BACKEND_URL}/api/push/vapid-public-key`);
      const vapidPublicKey = vapidResponse.data.publicKey;

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      await axios.post(`${BACKEND_URL}/api/push/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
        },
        user_agent: navigator.userAgent
      });

      setSubscribed(true);
      setShowPrompt(false);
      console.log('[Push] Bildirimler başarıyla etkinleştirildi');
      
      // Show success notification
      registration.showNotification('Bildirimler Etkin!', {
        body: 'Yeni ürün ve kampanyalardan haberdar olacaksınız.',
        icon: '/logo-ortadogu-ziraat.png',
        badge: '/favicon.png',
        tag: 'subscription-success'
      });

    } catch (error) {
      console.error('[Push] Subscription hatası:', error);
      alert('Bildirimler etkinleştirilemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setLoading(true);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe();

        // Notify backend
        await axios.delete(`${BACKEND_URL}/api/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`);

        setSubscribed(false);
        console.log('[Push] Bildirimler devre dışı bırakıldı');
      }
    } catch (error) {
      console.error('[Push] Unsubscribe hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Don't show again for 7 days
    localStorage.setItem('push-notification-dismissed', Date.now().toString());
  };

  // Don't render if notifications not supported
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return null;
  }

  return (
    <>
      {/* Floating button - always visible */}
      {permission === 'granted' && (
        <button
          onClick={subscribed ? unsubscribeFromPush : subscribeToPush}
          disabled={loading}
          className="fixed bottom-24 right-4 z-40 p-4 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
          title={subscribed ? 'Bildirimleri Kapat' : 'Bildirimleri Aç'}
        >
          {loading ? (
            <div className="w-6 h-6 border-2 border-[#8AC73E] border-t-transparent rounded-full animate-spin" />
          ) : subscribed ? (
            <Bell className="w-6 h-6 text-[#8AC73E]" />
          ) : (
            <BellOff className="w-6 h-6 text-gray-400 group-hover:text-[#8AC73E]" />
          )}
        </button>
      )}

      {/* Permission Prompt Modal */}
      {showPrompt && permission === 'default' && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-in fade-in duration-200"
            onClick={handleDismiss}
          />
          
          <div className="fixed bottom-0 left-0 right-0 md:bottom-6 md:left-1/2 md:-translate-x-1/2 md:max-w-md z-50 animate-in slide-in-from-bottom duration-300">
            <div className="bg-white rounded-t-3xl md:rounded-3xl shadow-2xl p-6 mx-4 md:mx-0 relative">
              <button
                onClick={handleDismiss}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Kapat"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>

              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#8AC73E] to-[#6B9E2E] rounded-2xl flex items-center justify-center shadow-lg">
                  <Bell className="w-8 h-8 text-white" />
                </div>
              </div>

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Bildirimleri Etkinleştir
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Yeni ürünler, kampanyalar ve özel fırsatlardan haberdar olmak için bildirimlere izin verin.
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-green-600 text-sm font-bold">✓</span>
                  </div>
                  <p className="text-sm text-gray-700">Yeni ürün lanşmanlarından ilk siz haberdar olun</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-green-600 text-sm font-bold">✓</span>
                  </div>
                  <p className="text-sm text-gray-700">Özel kampanyalar ve indirimler</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-green-600 text-sm font-bold">✓</span>
                  </div>
                  <p className="text-sm text-gray-700">Mevsimsel ekim rehberleri ve hatırlatmalar</p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleDismiss}
                  className="flex-1 px-6 py-3 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
                >
                  Şimdi Değil
                </button>
                <button
                  onClick={subscribeToPush}
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-[#8AC73E] to-[#6B9E2E] text-white font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Bell className="w-5 h-5" />
                      Etkinleştir
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PushNotificationManager;
